import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@stitches/react';
import AnimatedSection from '../../components/common/AnimatedSection';

// 页面样式组件
const PolicyContainer = styled('div', {
  padding: '4rem 2rem',
  maxWidth: '1000px',
  margin: '0 auto',
});

const PolicyHeader = styled('div', {
  textAlign: 'center',
  marginBottom: '3rem',
});

const PolicyTitle = styled('h1', {
  fontSize: '2.5rem',
  marginBottom: '1rem',
  fontWeight: 700,
});

const PolicyLastUpdated = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  fontStyle: 'italic',
});

const PolicySection = styled('section', {
  marginBottom: '2.5rem',
});

const SectionTitle = styled('h2', {
  fontSize: '1.8rem',
  marginBottom: '1.5rem',
  paddingBottom: '0.5rem',
  borderBottom: '2px solid $borderLight',
  fontWeight: 600,
});

const SectionContent = styled('div', {
  fontSize: '1.1rem',
  lineHeight: 1.7,
  color: '$textPrimary',
  
  p: {
    marginBottom: '1rem',
  },
  
  ul: {
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  },
  
  li: {
    marginBottom: '0.5rem',
  },
  
  a: {
    color: '$primary',
    textDecoration: 'none',
    
    '&:hover': {
      textDecoration: 'underline',
    }
  }
});

const SubSectionTitle = styled('h3', {
  fontSize: '1.4rem',
  marginTop: '2rem',
  marginBottom: '1rem',
  fontWeight: 500,
});

/**
 * 隐私政策页面组件
 * 展示平台的隐私政策和数据处理原则
 */
const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <PolicyContainer>
      <AnimatedSection>
        <PolicyHeader>
          <PolicyTitle>{t('privacy.title')}</PolicyTitle>
          <PolicyLastUpdated>{t('privacy.lastUpdated', { date: '2023-04-01' })}</PolicyLastUpdated>
        </PolicyHeader>
      </AnimatedSection>
      
      <AnimatedSection delay={0.1}>
        <PolicySection>
          <SectionTitle>{t('privacy.introduction.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.introduction.paragraph1')}</p>
            <p>{t('privacy.introduction.paragraph2')}</p>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.2}>
        <PolicySection>
          <SectionTitle>{t('privacy.dataCollection.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.dataCollection.paragraph1')}</p>
            <SubSectionTitle>{t('privacy.dataCollection.personalInfo.title')}</SubSectionTitle>
            <ul>
              <li>{t('privacy.dataCollection.personalInfo.item1')}</li>
              <li>{t('privacy.dataCollection.personalInfo.item2')}</li>
              <li>{t('privacy.dataCollection.personalInfo.item3')}</li>
              <li>{t('privacy.dataCollection.personalInfo.item4')}</li>
            </ul>
            <SubSectionTitle>{t('privacy.dataCollection.usage.title')}</SubSectionTitle>
            <ul>
              <li>{t('privacy.dataCollection.usage.item1')}</li>
              <li>{t('privacy.dataCollection.usage.item2')}</li>
              <li>{t('privacy.dataCollection.usage.item3')}</li>
            </ul>
            <p>{t('privacy.dataCollection.paragraph2')}</p>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.3}>
        <PolicySection>
          <SectionTitle>{t('privacy.cookies.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.cookies.paragraph1')}</p>
            <p>{t('privacy.cookies.paragraph2')}</p>
            <ul>
              <li>{t('privacy.cookies.item1')}</li>
              <li>{t('privacy.cookies.item2')}</li>
              <li>{t('privacy.cookies.item3')}</li>
            </ul>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.4}>
        <PolicySection>
          <SectionTitle>{t('privacy.dataSharing.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.dataSharing.paragraph1')}</p>
            <p>{t('privacy.dataSharing.paragraph2')}</p>
            <ul>
              <li>{t('privacy.dataSharing.item1')}</li>
              <li>{t('privacy.dataSharing.item2')}</li>
              <li>{t('privacy.dataSharing.item3')}</li>
              <li>{t('privacy.dataSharing.item4')}</li>
            </ul>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.5}>
        <PolicySection>
          <SectionTitle>{t('privacy.security.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.security.paragraph1')}</p>
            <p>{t('privacy.security.paragraph2')}</p>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.6}>
        <PolicySection>
          <SectionTitle>{t('privacy.userRights.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.userRights.paragraph1')}</p>
            <ul>
              <li>{t('privacy.userRights.item1')}</li>
              <li>{t('privacy.userRights.item2')}</li>
              <li>{t('privacy.userRights.item3')}</li>
              <li>{t('privacy.userRights.item4')}</li>
              <li>{t('privacy.userRights.item5')}</li>
            </ul>
            <p>{t('privacy.userRights.paragraph2')}</p>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.7}>
        <PolicySection>
          <SectionTitle>{t('privacy.changes.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.changes.paragraph1')}</p>
            <p>{t('privacy.changes.paragraph2')}</p>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.8}>
        <PolicySection>
          <SectionTitle>{t('privacy.contact.title')}</SectionTitle>
          <SectionContent>
            <p>{t('privacy.contact.paragraph1')}</p>
            <p>{t('privacy.contact.paragraph2', { email: 'privacy@BITENGINE.com' })}</p>
          </SectionContent>
        </PolicySection>
      </AnimatedSection>
    </PolicyContainer>
  );
};

export default PrivacyPolicy; 